import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, CheckCircle2, Loader2 } from "lucide-react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { formatApiError } from "@/lib/api";
import { Eyebrow } from "@/components/ui-bits";
import { GoogleTradeButton } from "@/components/GoogleTradeButton";

function Field({ label, testId, type = "text", value, onChange, required }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-ei-navy">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="mt-1 w-full border border-ei-mist rounded-sm px-3 py-2.5 text-sm text-ei-graphite focus:outline-none focus:border-ei-navy"
      />
    </label>
  );
}

export default function TradeSignup() {
  const { register } = useCustomerAuth();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", password: "", company_website: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      setDone(true);
      toast.success("Account created — check your email to verify");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-ei-white border-y border-ei-mist" id="trade-account">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div>
          <Eyebrow>Trade accounts</Eyebrow>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-ei-navy">Create a trade account</h2>
          <p className="mt-4 text-base text-ei-graphite/85 leading-relaxed" data-testid="trade-signup-eligibility">
            Apply for a trade account as an electrical contracting business, builder or property developer. Builder and
            developer applicants must nominate the electrical contractor responsible for installation. Our team
            reviews eligibility before access is granted.
          </p>
          <p className="mt-4 text-sm text-ei-graphite/70">
            Already registered?{" "}
            <Link to="/account/login" className="font-semibold text-ei-navy hover:text-ei-golddark" data-testid="trade-login-link">
              Sign in to your account
            </Link>
          </p>
        </div>

        <div className="bg-white border border-ei-mist rounded-md p-6 sm:p-8">
          {done ? (
            <div className="text-center py-6" data-testid="signup-success">
              <CheckCircle2 className="w-12 h-12 text-ei-golddark mx-auto" strokeWidth={1.5} />
              <h3 className="mt-4 text-lg font-semibold text-ei-navy">Thanks — please verify your email</h3>
              <p className="mt-2 text-sm text-ei-graphite/75">
                We've emailed you a verification link — please click it to confirm your address. After that, our team
                reviews your details and enables your login. We'll be in touch by email.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="trade-signup-form">
              <GoogleTradeButton label="Sign up with Google" testId="trade-signup-google" />
              <div className="flex items-center gap-3" aria-hidden="true">
                <span className="h-px flex-1 bg-ei-mist" />
                <span className="text-xs text-ei-graphite/50">or register with email</span>
                <span className="h-px flex-1 bg-ei-mist" />
              </div>
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.company_website}
                onChange={set("company_website")}
                className="hidden"
                aria-hidden="true"
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your name" testId="signup-name" value={form.name} onChange={set("name")} required />
                <Field label="Business name" testId="signup-company" value={form.company} onChange={set("company")} required />
              </div>
              <Field label="Work email" type="email" testId="signup-email" value={form.email} onChange={set("email")} required />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Mobile" testId="signup-phone" value={form.phone} onChange={set("phone")} required />
                <Field label="Password" type="password" testId="signup-password" value={form.password} onChange={set("password")} required />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="signup-submit"
                className="w-full font-display text-sm font-bold bg-ei-navy text-ei-white px-6 py-3.5 rounded-sm hover:bg-ei-ink transition-colors inline-flex items-center justify-center gap-2 min-h-[48px] disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Create account
              </button>
              <p className="text-xs text-ei-graphite/55">Password must be at least 8 characters. Trade customers only.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
